/**
 * Pipeline board (A7Y-5209): `/admin/cmo/crm/board`.
 *
 * Pure layout for the board page: one card per account from
 * `GET /admin/crm/accounts`, in a column per active stage (Lead → Onboarded),
 * with partners and paused or closed accounts listed below. It replaces the
 * artifact the GTM rituals redrew from a script (A7Y-5189) and keeps that
 * board's rules: who an account runs through, the 30-day quiet flag, the
 * small stage tag, and a "waits on a trigger" next date for an account with an
 * open trigger item (A7Y-5204, with its date), or with a watch when it has none.
 * Stage notes, next-action text and history never reach a card.
 */
import type { CrmAccountListItem, CrmPriority, CrmSegment, CrmStage } from '@a7y/types';

/** The board's columns, earliest first. */
export const BOARD_STAGES: readonly CrmStage[] = [
  'lead',
  'conversation',
  'demo',
  'trial',
  'proposal',
  'signed',
  'onboarded',
];

/** An active account untouched for this many days or more is flagged quiet. */
export const QUIET_DAYS = 30;

/**
 * The CRM has no owner field. These accounts run through Enric or Thomas;
 * every other account is Yevhen's. Matched on the account name, or on the name
 * without its parentheses ("Newry/ZED (…)" → "Newry/ZED").
 */
export const RUNS_THROUGH_ENRIC: ReadonlySet<string> = new Set([
  'IGG',
  'King',
  'Activision',
  'Havas',
  'Newry/ZED',
  'First Fun',
  'Supercell',
  'Papaya Games',
  'Tencent',
  'Ubisoft',
  'Dream Games',
  'BoomBit',
  'Voodoo',
  'Wewoo',
  'Lead Tech',
  'App-Growing',
  'Rovio Entertainment',
  'FunPlus',
]);
export const RUNS_THROUGH_THOMAS: ReadonlySet<string> = new Set(['Newly']);

export type Owner = 'Yevhen' | 'Enric' | 'Thomas';
export const OWNERS: readonly Owner[] = ['Yevhen', 'Enric', 'Thomas'];

/** The small tags a stage note can open with, carried over from the scoreboard's stage cell. */
const SUB_TAGS = ['Revived', 'Pilot', 'Affiliate'] as const;

export interface BoardCard {
  id: string;
  name: string;
  /** Current contacts, primary first, departed left out; empty when there are none. */
  contacts: string[];
  stage: CrmStage;
  /** "Revived", "Pilot", "Affiliate", "Dormant" or "Deprecated"; null for none. */
  tag: string | null;
  segment: CrmSegment;
  owner: Owner;
  lastTouch: string | null;
  /** Days since the last touch; null when no touch is recorded. */
  daysSinceTouch: number | null;
  quiet: boolean;
  nextDate: string | null;
  /** The account waits on an event: an open trigger item (A7Y-5204), or a watch. */
  waitsOnTrigger: boolean;
  /** The open trigger item's date fallback; null when the wait is a watch without a date. */
  triggerDue: string | null;
  /** A7Y-5203: the priority rank; null when not ranked. */
  rank: CrmPriority | null;
}

/** `(…)` groups removed, whitespace collapsed. */
function withoutParens(text: string): string {
  let prev = '';
  let out = text;
  while (prev !== out) {
    prev = out;
    out = out.replace(/\([^()]*\)/g, '');
  }
  return out.replace(/\s+/g, ' ').trim();
}

export function ownerOf(name: string): Owner {
  const short = withoutParens(name) || name;
  if (RUNS_THROUGH_ENRIC.has(name) || RUNS_THROUGH_ENRIC.has(short)) return 'Enric';
  if (RUNS_THROUGH_THOMAS.has(name) || RUNS_THROUGH_THOMAS.has(short)) return 'Thomas';
  return 'Yevhen';
}

/** The first word of a stage note, without markdown emphasis or punctuation around it. */
function firstWord(note: string | null): string {
  if (!note) return '';
  return (
    note
      .replace(/\*\*|__|~~/g, '')
      .trim()
      .split(/\s+/)[0]
      ?.replace(/^\(+/, '')
      .replace(/[,:;)]+$/, '') ?? ''
  );
}

export function tagOf(stage: CrmStage, stageNote: string | null): string | null {
  const word = firstWord(stageNote).toLowerCase();
  if (stage === 'dormant') return word === 'deprecated' ? 'Deprecated' : 'Dormant';
  if (stage === 'closed') return null;
  return SUB_TAGS.find((t) => t.toLowerCase() === word) ?? null;
}

/** Whole days from `from` to `to`, both `YYYY-MM-DD`. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

export function isActive(stage: CrmStage): boolean {
  return BOARD_STAGES.includes(stage);
}

export function cardOf(item: CrmAccountListItem, today: string): BoardCard {
  const { account } = item;
  const primaryId =
    item.primaryContact && !item.primaryContact.departed ? item.primaryContact.id : null;
  const current = item.contacts.filter((c) => !c.departed);
  const ordered = [
    ...current.filter((c) => c.id === primaryId),
    ...current.filter((c) => c.id !== primaryId),
  ];
  const daysSinceTouch = account.lastTouch ? daysBetween(account.lastTouch, today) : null;
  return {
    id: account.id,
    name: account.name,
    contacts: ordered.map((c) => c.name).filter((n) => n !== account.name),
    stage: account.stage,
    tag: tagOf(account.stage, account.stageNote),
    segment: account.segment,
    owner: ownerOf(account.name),
    lastTouch: account.lastTouch,
    daysSinceTouch,
    quiet: isActive(account.stage) && daysSinceTouch !== null && daysSinceTouch >= QUIET_DAYS,
    nextDate: item.nextDate,
    waitsOnTrigger: item.trigger !== null || Boolean(account.watch?.trim()),
    triggerDue: item.trigger?.due ?? null,
    rank: account.priority ?? null,
  };
}

export type SegmentFilter = 'all' | 'enterprise' | 'mid' | 'agency';
export interface BoardFilters {
  segment: SegmentFilter;
  owner: Owner | 'all';
  quietOnly: boolean;
}
export const ALL_CARDS: BoardFilters = { segment: 'all', owner: 'all', quietOnly: false };

function passes(card: BoardCard, filters: BoardFilters): boolean {
  if (filters.segment !== 'all' && card.segment !== filters.segment) return false;
  if (filters.owner !== 'all' && card.owner !== filters.owner) return false;
  if (filters.quietOnly && !card.quiet) return false;
  return true;
}

/** Most recently touched first; never-touched last; ties by name. */
export function byRecency(a: BoardCard, b: BoardCard): number {
  if (a.lastTouch !== b.lastTouch) {
    if (a.lastTouch === null) return 1;
    if (b.lastTouch === null) return -1;
    return a.lastTouch < b.lastTouch ? 1 : -1;
  }
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}

export interface BoardLayout {
  /** One column per `BOARD_STAGES` entry, customers only (no partners). */
  columns: { stage: CrmStage; cards: BoardCard[] }[];
  /** Partners at an active stage, furthest stage first. */
  partners: BoardCard[];
  /** Dormant and closed accounts; hidden while "quiet only" is on, since they cannot be quiet. */
  parked: BoardCard[];
  totals: {
    customersInPlay: number;
    quietCustomers: number;
    partnersInPlay: number;
    parked: number;
  };
}

/** The board for `items` under `filters`; `totals` always count every account, unfiltered. */
export function layoutBoard(
  items: readonly CrmAccountListItem[],
  filters: BoardFilters,
  today: string
): BoardLayout {
  const cards = items.map((item) => cardOf(item, today));
  const shown = cards.filter((c) => passes(c, filters));
  const active = (c: BoardCard) => isActive(c.stage);
  const customer = (c: BoardCard) => c.segment !== 'partner';
  return {
    columns: BOARD_STAGES.map((stage) => ({
      stage,
      cards: shown.filter((c) => c.stage === stage && customer(c)).sort(byRecency),
    })),
    partners: shown
      .filter((c) => active(c) && !customer(c))
      .sort(
        (a, b) => BOARD_STAGES.indexOf(b.stage) - BOARD_STAGES.indexOf(a.stage) || byRecency(a, b)
      ),
    parked: filters.quietOnly ? [] : shown.filter((c) => !active(c)).sort(byRecency),
    totals: {
      customersInPlay: cards.filter((c) => active(c) && customer(c)).length,
      quietCustomers: cards.filter((c) => active(c) && customer(c) && c.quiet).length,
      partnersInPlay: cards.filter((c) => active(c) && !customer(c)).length,
      parked: cards.filter((c) => !active(c)).length,
    },
  };
}

export type NextLine =
  | { kind: 'trigger'; date: string | null }
  | { kind: 'none' }
  | { kind: 'today' }
  | { kind: 'overdue'; date: string }
  | { kind: 'upcoming'; date: string };

/**
 * What a card says about its next date: the same date the account page shows (A7Y-5342 —
 * the board used to prefer a trigger, so Pickem read "by 2 Nov" here and 6 Oct there).
 * Only when that date is the trigger's own (or there is none) does the line become the trigger.
 */
export function nextLine(card: BoardCard, today: string): NextLine {
  if (card.waitsOnTrigger && (!card.nextDate || card.nextDate === card.triggerDue)) {
    return { kind: 'trigger', date: card.triggerDue };
  }
  if (!card.nextDate) return { kind: 'none' };
  if (card.nextDate === today) return { kind: 'today' };
  return card.nextDate < today
    ? { kind: 'overdue', date: card.nextDate }
    : { kind: 'upcoming', date: card.nextDate };
}

/** The trigger an account also waits on, when the next line is a dated item; null otherwise. */
export function triggerLine(card: BoardCard, today: string): { fallback: string | null } | null {
  if (!card.waitsOnTrigger || nextLine(card, today).kind === 'trigger') return null;
  return { fallback: card.triggerDue };
}

/** A trigger's fallback as the Board prints it (A7Y-5873): a past one "was due", as a dated item's does. */
export function fallbackLabel(date: string, today: string): string {
  return date < today ? `was due ${shortDate(date, today)}` : `fallback ${shortDate(date, today)}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "8 Oct", with the year when it is not `today`'s year. */
export function shortDate(iso: string, today: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const year = iso.slice(0, 4) === today.slice(0, 4) ? '' : ` ${y}`;
  return `${d} ${MONTHS[m - 1]}${year}`;
}
